import { useMemo } from "react";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, X } from "lucide-react";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { useUpdateListOrder } from "../../hooks/mutations/useUpdateListOrder";
import {
  arrangeByPlacement,
  type ListlessGroupPlacement,
} from "../../lib/listlessGroupPlacement";
import { interpolatedOrderPosition } from "../../lib/orderPosition";
import type { List } from "../../types";

interface ReorderListsDialogProps {
  lists: List[];
  listlessCount: number;
  placement: ListlessGroupPlacement;
  onPlacementChange: (placement: ListlessGroupPlacement) => void;
  onClose: () => void;
}

interface SortableListRowProps {
  list: List;
}

function SortableListRow({ list }: SortableListRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: list.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2.5 px-3 py-2.5 bg-warm-bg-elevated border border-warm-border rounded-soft"
    >
      <button
        type="button"
        className="text-warm-text-tertiary cursor-grab active:cursor-grabbing flex-shrink-0 p-0.5 rounded-soft hover:text-warm-text-primary"
        aria-label={`Reorder ${list.name}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical size={16} />
      </button>
      <span className="text-base leading-none" aria-hidden="true">
        {list.emoji}
      </span>
      <span className="text-sm font-medium text-warm-text-primary">
        {list.name}
      </span>
    </li>
  );
}

function NoListRow({ count }: { count: number }) {
  return (
    <li className="flex items-center gap-2.5 px-3 py-2.5 border border-dashed border-warm-border rounded-soft">
      <span className="text-sm font-medium text-warm-text-secondary">
        No list
      </span>
      <span className="ml-auto text-xs text-warm-text-tertiary tabular-nums">
        {count === 1 ? "1 task" : `${count} tasks`}
      </span>
    </li>
  );
}

export default function ReorderListsDialog({
  lists,
  listlessCount,
  placement,
  onPlacementChange,
  onClose,
}: ReorderListsDialogProps) {
  useEscapeKey(onClose);
  const updateListOrder = useUpdateListOrder();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const listIds = useMemo(() => lists.map((list) => list.id), [lists]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const draggedIndex = lists.findIndex((list) => list.id === active.id);
    const targetIndex = lists.findIndex((list) => list.id === over.id);
    if (draggedIndex === -1 || targetIndex === -1) return;

    updateListOrder.mutate({
      listId: String(active.id),
      newOrderPosition: Math.floor(
        interpolatedOrderPosition(lists, draggedIndex, targetIndex),
      ),
    });
  };

  const noListRow = <NoListRow key="__no_list__" count={listlessCount} />;

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-30 p-2 sm:p-4 animate-dialog-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-labelledby="reorder-lists-title"
        aria-modal="true"
        className="bg-warm-bg-card rounded-xl border border-warm-border p-4 sm:p-6 w-full max-w-lg max-h-[95vh] sm:max-h-[80vh] overflow-y-auto animate-dialog-panel"
      >
        <div className="flex items-center justify-between mb-4">
          <h2
            id="reorder-lists-title"
            className="font-heading text-lg text-warm-text-primary"
          >
            Reorder lists
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-warm-text-tertiary hover:text-warm-text-primary transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div role="group" aria-label="Tasks with no list">
          <p className="text-sm font-medium text-warm-text-primary mb-2">
            Tasks with no list
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              aria-pressed={placement === "start"}
              onClick={() => onPlacementChange("start")}
              className={`flex-1 px-3 py-2 rounded-soft text-sm font-medium border transition-colors ${
                placement === "start"
                  ? "border-warm-blue bg-warm-blue/10 text-warm-blue"
                  : "border-warm-border text-warm-text-secondary hover:bg-warm-bg-elevated"
              }`}
            >
              Start of board
            </button>
            <button
              type="button"
              aria-pressed={placement === "end"}
              onClick={() => onPlacementChange("end")}
              className={`flex-1 px-3 py-2 rounded-soft text-sm font-medium border transition-colors ${
                placement === "end"
                  ? "border-warm-blue bg-warm-blue/10 text-warm-blue"
                  : "border-warm-border text-warm-text-secondary hover:bg-warm-bg-elevated"
              }`}
            >
              End of board
            </button>
          </div>
        </div>

        <p className="mt-5 mb-2 text-sm font-medium text-warm-text-primary">
          List order
        </p>
        {lists.length === 0 ? (
          <p className="text-sm text-warm-text-secondary">
            You have no lists yet. Add a list from Settings.
          </p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={listIds}
              strategy={verticalListSortingStrategy}
            >
              <ul className="space-y-2">
                {arrangeByPlacement(
                  lists.map((list) => (
                    <SortableListRow key={list.id} list={list} />
                  )),
                  [noListRow],
                  placement,
                )}
              </ul>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
}
